import React from 'react';
import moment from 'moment';

import {Button, Dialogue, Icon, Modal, Text} from '@albert-io/atomic';
import ConfirmActionModal from 'client/components/ConfirmActionModal/ConfirmActionModal.react';
import CustomizeReassignModal from 'client/components/CustomizeReassignModal/CustomizeReassignModal.react';
import {AddAssignmentQuestionSetsToFolderModal} from 'client/Classrooms/Classroom/Teacher/Assignments/AssignmentsList/AssignmentRowOptions.react';
import {invalidatePartialInterest, genericMandarkRequest} from 'resources/mandark.resource';

import {
  buildDeleteRequest,
  getDeleteConfirmationCopy,
  getInvalidateResourcePath
} from './AssignmentMenu.helpers';

export type AssignmentModalType = 'details' | 'delete' | 'reassign' | 'folder' | 'archived' | null;

interface AssignmentActionModalsProps {
  modal: AssignmentModalType;
  onClose: () => void;
  assignmentId: string;
  assignmentName: string;
  isDraft: boolean;
  /** Class-scoped delete only unassigns this class instead of deleting the assignment. */
  classroomId?: string;
  /** Resolved creator name for the details modal (fetched by the menu before opening). */
  creatorName: string | null;
  updatedAt: string;
  /** Question counts for "Save to Folder"; archivedQuestionCount drives filter_archived on save. */
  questionCount: number;
  archivedQuestionCount: number;
  onDeleted: () => void;
}

export const AssignmentActionModals = ({
  modal,
  onClose,
  assignmentId,
  assignmentName,
  isDraft,
  classroomId,
  creatorName,
  updatedAt,
  questionCount,
  archivedQuestionCount,
  onDeleted
}: AssignmentActionModalsProps) => {
  const deleteAssignment = async () => {
    const {resourcePath, body} = buildDeleteRequest({assignmentId, classroomId, isDraft});
    await genericMandarkRequest('delete', {resourcePath}, body);
  };

  const handleDeleteResolved = () => {
    invalidatePartialInterest({resourcePath: getInvalidateResourcePath(isDraft)});
    onClose();
    onDeleted();
  };

  const deleteCopy = getDeleteConfirmationCopy({assignmentName, classroomId, isDraft});

  if (modal === 'details') {
    return (
      <Modal handleClose={onClose} ariaLabel='Document Details'>
        {({modalContentStyle}) => (
          <Dialogue inModal handleClose={onClose} size='s' className={modalContentStyle}>
            <Text size='m' bold>
              Document Details
            </Text>
            <div className='mt-x3 mb-x2 flex items-center gap-x1'>
              <Icon icon='user' iconStyle='regular' />
              Created by: {creatorName}
            </div>
            <div className='flex items-center gap-x1'>
              <Icon icon='calendar' iconStyle='regular' />
              Last modified {moment(updatedAt).format('MMM D, YYYY')}
            </div>
          </Dialogue>
        )}
      </Modal>
    );
  }

  if (modal === 'delete') {
    return (
      <ConfirmActionModal
        title='Are you sure?'
        align='center'
        handleClose={onClose}
        makeRequest={deleteAssignment}
        onResolve={handleDeleteResolved}
      >
        {({isRequestInflight, onCancel, onConfirm}) => (
          <>
            <Dialogue.Body align='center'>{deleteCopy}</Dialogue.Body>
            <Dialogue.BtnGroup align='center'>
              <Button color='secondary' disabled={isRequestInflight} onClick={onCancel}>
                Cancel
              </Button>
              <Button color='negative' disabled={isRequestInflight} onClick={onConfirm}>
                Delete
              </Button>
            </Dialogue.BtnGroup>
          </>
        )}
      </ConfirmActionModal>
    );
  }

  if (modal === 'reassign') {
    return (
      <CustomizeReassignModal
        assignmentId={assignmentId}
        assignmentName={assignmentName}
        handleClose={onClose}
      />
    );
  }

  if (modal === 'folder') {
    return (
      <AddAssignmentQuestionSetsToFolderModal
        assignmentId={assignmentId}
        handleClose={onClose}
        questionCount={questionCount}
        isDraft={isDraft}
        archivedQuestionCount={archivedQuestionCount}
      />
    );
  }

  return null;
};
